import { Component, OnInit, ViewChild, ElementRef, Renderer2, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, Input } from '@angular/core';
import { Store, Select, Actions, ofActionDispatched, ofActionCompleted } from '@ngxs/store';
import { 
  SetReady, 
  SetEngineChapterInit,
  SynchronizeVisibleNodes,
  SetEngineChapter, 
  ClickNode, 
  FlyToObject,
  SynchronizeRenderableNodes,
  ToggleAnimation,
  ShowScrollAlert,
  SynchronizePickableNodes,
  HoverNode,
  PlayChapterFromStart,
  PlayChapterAnimation,
  SetAnimationPlaying,
  SetContextMenu
  } from '../../state/engine.actions';
import { EngineState } from '../../state/engine.state';
import { Observable, Subscription, fromEvent, BehaviorSubject, combineLatest, Subject, interval, merge } from 'rxjs';
import { filter, takeUntil, bufferTime, debounceTime, pairwise, map, distinctUntilChanged, auditTime } from 'rxjs/operators';
import { EngineService } from '../../engine.service';
import { environment } from 'src/environments/environment';
import { EnginePositionModel } from '../../models/engine-position.model';
import { CameraService } from '../../camera.service';
import { EngineVisibilityEnum } from '../../models/engine-visibility.enum';
import { ViewTreeNode } from '../../models/view.tree-node';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { HitModel } from '../../models/hit.model';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { ChapterInitModel } from '../../models/engine-chapter-init-model';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { BdGraph, BdGraphStateMap } from 'src/lib/graph/graph.model';
import { PaintService } from '../../paint.service';
import {
  SetCamera,
  MoveCamera,
  PanCameraStatic,
  RotateCameraStatic,
  ZoomCameraStatic,
  SetCameraSuccess,
  SetCameraFlyTo
} from '../../state/camera/engine.camera.actions';
import { CameraMotionModel } from '../../models/camera-motion.model';
import { EngineCameraState } from '../../state/camera/engine.camera.state';
import { LoadProgressModel } from '../../models/load-progress.model';
import { AnimationService } from '../../animation.service';
import { BackgroundModel } from '../../models/background.model';
import { EngineNodesState } from '../../state/nodes/engine.nodes.state';
import { CameraModel } from '../../models/camera.model';
import { EventsEnum } from '../../models/events.enum';
import { StoryState } from 'src/app/story/state/story.state';
import { SetProgress } from '../../state/progress/engine.progress.actions';
import { LoadStoryChunkSuccess } from 'src/app/story/state/story.actions';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { PaintPresetFade, PaintPresetOriginal } from '../../models/paint-presets.const';
import { ScrollModeEnum } from '../../models/scroll-mode.enum';
import { disableXrayMode } from 'src/app/shared/xray-mode';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { NodeActions } from '../../state/nodes/engine.nodes.actions';
import { NodeState, ObjectId } from '../../state/nodes/engine.nodes.state.model';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';
import { ClipDirection, ClippingPlane, ClipBoundaries, ClippingPlanes } from 'src/app/story/models/clipping-plane.model';

import { get, isEmpty, isNil } from 'lodash';
import * as Hammer from 'hammerjs';
import { ChapterReadyStateModel } from 'src/app/story/models/chapter-ready-state.model';
import { ParticleStateListener } from '../../classes/particle-state-listener';

import { VisibilityLayersService } from 'src/app/story/visibility-layers.service'; 
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { MediaService } from 'src/app/story/media.service';
import { Howl } from 'howler';
import { SaveBookmark } from 'src/app/bookmarks/state/bookmarks.actions';
import { ChapterService } from 'src/app/story/chapter.service';

declare const Human: any;

enum KeyboardCamera {
  Pan = 0.4, 
  PanSlow = 0.04,
  Zoom = 5,
  ZoomSlow = 1,
  Rotate = 0.3,
  RotateSlow = 0.03
}

enum MouseCamera {
  Rotate = 6,
  Pan = 1.75,
  Zoom = 2.5
}

enum TouchCamera {
  Zoom = -6,
  TrackZoom = 0.5,
  Pan = 1.25,
  Rotate = 1.5,
  Friction = 0.85,
  ZoomFriction = 0.85,
  FrictionThreshold = 0.002,
}

enum CursorClasses {
  Hide = 'engine--cursor-hide',
  Label = 'engine--cursor-label',
  Paint = 'engine--cursor-paint',
  Crosshair = 'engine--cursor-crosshair',
  Default = ''
}

enum MouseButtons {
  None = 0,
  Left = 1,
  Right = 2,
  LeftAndRight = 4
}

const timeElapsedAtSixtyFPS = 1000 / 60.0;

// cap the force multiplier between 0 and 15 (15 would indicate 4 fps)
const maxForceMultiplier = 15.0;

@Component({
  selector: 'app-engine',
  templateUrl: './engine.component.html',
  styleUrls: ['./engine.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EngineComponent implements OnInit, OnDestroy {
  mouseMove$: Observable<MouseEvent>;
  mouseLeave$: Observable<MouseEvent>;

  nodeSelectedInterceptor: Subscription;
  flyCamera = true;
  chapterActivating = false;
  visible = false;
  application = EnvironmentApplication;
  viewGraph: BdGraph<ViewTreeNode>;
  cursorClass: CursorClasses = CursorClasses.Default;

  private ngUnsubscribe = new Subject();

  @ViewChild('engine', { static: true }) engineElement: ElementRef;
  @ViewChild('engineCanvas', { static: true }) engineCanvasElement: ElementRef;
  @ViewChild('engineAnnotations', { static: true }) engineAnnotationsElement: ElementRef;
  
  // Engine
  @Select(EngineState.getReady)
  ready$: Observable<boolean>;

  @Select(EngineState.getViewGraph)
  viewGraph$: Observable<BdGraph<ViewTreeNode>>;

  @Select(EngineState.getEnginePosition)
  enginePosition$: Observable<EnginePositionModel>;

  @Select(EngineState.getHoveredNode)
  hoveredNode$: Observable<ViewTreeNode>;

  @Select(EngineState.getChapter)
  chapter$: Observable<string>;

  @Select(EngineState.getChapterInit)
  chapterInit$: Observable<ChapterInitModel>;

  @Select(EngineNodesState.getCheckedNodes)
  checkedNodes$: Observable<NodeState[]>;
  
  @Select(EngineState.getActiveTool)
  activeTool$: Observable<ToolEnum>;

  @Select(EngineState.getAnimationPlaying)
  animationPlaying$: Observable<boolean>;

  @Select(EngineState.getAutoplay)
  autoplay$: Observable<boolean>;

  @Select(EngineState.getPlayAudio)
  playAudio$: Observable<boolean>;

  @Select(EngineState.getDisplay)
  engineDisplay$: Observable<boolean>;
  display = false;

  @Select(EngineState.getVisible)
  engineVisible$: Observable<boolean>;
  engineVisible = true;

  @Select(EngineState.getShowProgressBar)
  showProgressBar$: Observable<boolean>;

  @Select(EngineState.getContextmenu)
  contextmenu$: Observable<any>;
  contextmenuNode: ViewTreeNode;
  contextmenuHit: HitModel;
  
  // Story - Chapters
  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;

  @Select(StoryChaptersState.getSelectedChapterId)
  selectedChapterId$: Observable<string>;

  @Select(StoryChaptersState.getSelectedChapterReadyState)
  readyState$: Observable<ChapterReadyStateModel>;

  @Select(StoryChaptersState.getSelectedChapterClippingPlanes)
  clippingPlanes$: Observable<ClippingPlanes>;

  // Camera
  @Select(EngineCameraState.getCameraMotion)
  cameraMotion$: Observable<CameraMotionModel>;

  // Background color
  @Select(StoryChaptersState.getSelectedChapterBackground)
  background$: Observable<BackgroundModel>;

  background = new BehaviorSubject<BackgroundModel>(null);
  particleStateListener: ParticleStateListener;

  ctrlPressed = false;
  scrollType: 'wheel' | 'trackpad' = null;
  scrollTimeout = null;
  pinchTicks = 0;
  progress = '0';
  deltaTime = window.performance.now();
  pinchMode = '';

  @Input()
  inputEnabled = true;


  @HostListener('wheel', ['$event'])
  wheel(event: WheelEvent) {
    
    const scrollMode = this.store.selectSnapshot(EngineState.getScrollMode);
    const ctrlActive = event.ctrlKey;

    if (scrollMode === ScrollModeEnum.DoNotCaptureShowMessage && !ctrlActive) {
      this.store.dispatch(new ShowScrollAlert(true));
    }

    if (scrollMode !== ScrollModeEnum.Capture && !ctrlActive) {
      return;
    }

    // if we're allowed to scroll, we never want the parent window to scroll
    event.preventDefault();
    this.handleWheel(event);
  }

  @HostListener('contextmenu', ['$event'])
  contextmenu(event: MouseEvent) {
    this.handleContextMenu(event);
  }

  @HostListener('mouseout', ['$event'])
  mouseout() {
    this.handleMouseOut();
  }

  constructor(
    private actions: Actions,
    private cameraService: CameraService,
    private chapterService: ChapterService,
    private changeDetecton: ChangeDetectorRef,
    private engineService: EngineService,
    private paintService: PaintService,
    private visibilityLayersService: VisibilityLayersService,
    private mediaService: MediaService,
    private renderer: Renderer2,
    private store: Store  ) {
    // Include pako when using Loft to track download size
    if (environment.application === EnvironmentApplication.Loft) {
      import('pako').then(pako => {
        window['pako'] = pako;
      });
    }
  }

  // map single property state map to graph state map
  private convertToLeafStateMap(property: string, propertyMap: {[key: string]: boolean} ): BdGraphStateMap {
    const leafStateMap: BdGraphStateMap = {};
    Object.entries(propertyMap).forEach(([id, state]) => {
      leafStateMap[id] = {};
      leafStateMap[id][property] = state;
    
    });
    return leafStateMap;
  }

  ngOnInit() {

    // init particle systems listener
    this.particleStateListener = new ParticleStateListener(this.store);

    this.bindEngineEvents();
    
    this.bindApplicationEvents();
    this.bindChapterChangeEvents();
    this.bindCameraEvents();
    this.bindToolEvents();

    this.bindMouseEvents();
    this.bindKeyEvents();
    this.bindHumanInteractions();
  
    this.initChangeListener();
    this.initChapterAudio();

    this.initBackgroundListener();
    this.initPlaybackSpeedListener();

    Human.properties.set({assetDomain: environment.assetDomain});
    Human.workers.setPath('/engine/studio/workers');
    Human.init.init();
  }

  ngOnDestroy() {
    this.changeDetecton.detach();
    this.particleStateListener.destroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initEventListeners() {
    
  }

  bindEngineEvents() {
    // Graph has initialized
    this.engineService.getEvent(EventsEnum.Loaded)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.store.dispatch(new SetReady(true));
      });
      
    // Chapters have started activating
    this.engineService.getEvent(EventsEnum.TimelineChaptersActivateStart)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.chapterActivating = true;
      });

    // Chapters have finished
    this.engineService.getEvent(EventsEnum.TimelineChaptersActivateFinish)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params) => {
        this.chapterActivating = false;

        const currentBranch = Human.timeline.activeRoot._nowBranch;
        const chapterInit = {
          id: currentBranch.id,
          init: true,
          camera: currentBranch.initializedCamera,      
          annotations: currentBranch.initializedAnnotations,
          graph: currentBranch.initializedGraph,
          properties: currentBranch.initializedProperties,
          particles: currentBranch.initializedParticles,
          clips: currentBranch.initializedClippingPlanes,
          postprocessing: currentBranch.initializedPostprocess,
          transparency: currentBranch.initializedTransparencyMode
        };
        this.store.dispatch(new SetEngineChapter(params.chapterId));
        this.store.dispatch(new SetEngineChapterInit(chapterInit));
      });

    // Handle Chapter Transition - trigger API events
    this.engineService
    .getEvent(EventsEnum.TimelineChaptersActivated)
    .pipe(
      takeUntil(this.ngUnsubscribe)
      )
    .subscribe((event) => {
      this.engineService.fireEvent(EventsEnum.LabelsChapterInfo, this.store.selectSnapshot(StoryLabelsState.getChapterLabels));
    });

    // Listen to timeline play / pause updates, and ensure the app stays in sync
    merge(
      this.engineService.getEvent(EventsEnum.TimelinePlayed),
      this.engineService.getEvent(EventsEnum.TimelinePaused)
    )
    .pipe(
      takeUntil(this.ngUnsubscribe),
      filter(() => this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId) != null),
      debounceTime(50),
      map(() => Human.timeline.playing)
    )
    .subscribe(playing => {
      this.store.dispatch(new SetAnimationPlaying(playing));
    });

    // Handle engine tick, but only when it's visible
    this.engineService.getEvent(EventsEnum.Tick)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (this.visible) {
          if (this.inputEnabled) {
            this.handleMouseInputEngineTick();
          }
          this.handleCameraTick();
          this.deltaTime = window.performance.now();
        }
      });
      
      this.engineService
        .getEvent(EventsEnum.TweenVisibilityUpdate)
        .pipe(
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(update => {
          const objectsToShow = Object.keys(update).filter(objectId => update[objectId] === true);
          const objectsToHide = Object.keys(update).filter(objectId => update[objectId] === false);

          if (objectsToShow.length > 0) {
            this.store.dispatch(
              new NodeActions.UpdateNodesShown(
                true,
                objectsToShow,
                undefined,
                false, // do not record
                true // mark as 'setByTween'
              )
            );
          }

          if (objectsToHide.length > 0) {
            this.store.dispatch(
              new NodeActions.UpdateNodesShown(
                false,
                objectsToHide,
                undefined,
                false, // do not record
                true // mark as 'setByTween'
              )
            );
          }
        
        });
    
    // Handle object renderable event
    this.engineService.getEvent(EventsEnum.SceneObjectRenderable)
      .pipe(
        bufferTime(1000),
        takeUntil(this.ngUnsubscribe))
      .subscribe((renderable: Array<any> ) => {
        if (renderable.length) {
          const leafStateMap = {};
          renderable.forEach(renderableData => {
            leafStateMap[renderableData.objectId] = {'renderable': true};
          });
          this.store.dispatch(new SynchronizeRenderableNodes(leafStateMap));
        }
      });

    // Handle Loading
    this.engineService
      .getEvent(EventsEnum.AssetsLoadProgress)
      .pipe(
        auditTime(33),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((progress: LoadProgressModel) => {
        
        const totalChunks = Math.max(1, this.store.selectSnapshot(StoryState.getLoadChunks).length);
        const remainingChunks = this.store.selectSnapshot(StoryState.getLoadChunksRemaining).length;
        const currentChunkIdx = totalChunks - remainingChunks;

        const { receivedAssets, requestedAssets } = progress;
        const currentChunkProgress = receivedAssets / requestedAssets;

        const chunkWidth = 1 / totalChunks;
        const baseProgress = currentChunkIdx * chunkWidth;
        const currentProgress = currentChunkProgress * chunkWidth;

        // #FIXME: There is a race condition where remainingChunks is sometimes 0 at the end of
        // loading when this is calculated, resulting in progress being 2.00. This is a temporary patch
        // to ensure we always hit 1.0
        const storyLoadProgress = remainingChunks === 0 ? 1 : currentProgress + baseProgress;

        this.progress = storyLoadProgress.toFixed(2);

        this.changeDetecton.markForCheck();

        this.store.dispatch(new SetProgress(storyLoadProgress, currentChunkProgress));
      });

  // There are story load chunks that don't actually initiate any downloading.
  // Therefore, we can only know when loading has completed by pipeing LoadStoryChunkSuccess, and there are zero chunks remaining
  this.actions
    .pipe(
      ofActionCompleted(LoadStoryChunkSuccess),
      takeUntil(this.ngUnsubscribe)
    )
    .subscribe(() => {
      const chunksRemaining = this.store.selectSnapshot(
        StoryState.getLoadChunksRemaining
      ).length;
  
      if (chunksRemaining === 0) {
        this.store.dispatch(new SetProgress(1, 1));
        this.engineService.enableRender();
      }
    });

  }

  bindApplicationEvents() {
    // Available screen space for the engine has changed
    this.enginePosition$
      .pipe(
        filter(position => position != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((position) => {
        this.repositionEngine(position);
      });

    // deal with turning xray mode on and off
    this.store.select(EngineState.getXrayModeEnabled)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(xrayModeEnabled => {

      // get checked and unchecked nodes.
      const checkedNodes = this.store.selectSnapshot(EngineNodesState.getCheckedNodes);
      const nodes = this.store.selectSnapshot(EngineNodesState.getNodes);

      // when toggling xray mode,
      // we have to refresh the way objects are presented, based on the currently selected nodes.
      if (xrayModeEnabled === false) {

        // re-paint leaf nodes
        Object.keys(nodes || []).forEach(objectId => {
          const recipe = nodes[objectId].paint;
          this.paintService.paint(objectId, recipe);
        });
          
        // re-highlight checked nodes
        checkedNodes.forEach(node => this.paintService.highlight(node.objectId, true));

      } else { // xray mode is enabled
        this.paintService.paintAll(Object.keys(nodes), PaintPresetFade.recipe);
        this.paintService.paintAll(checkedNodes.map(node => node.objectId), PaintPresetOriginal.recipe);
      }
    });
    
    // Play chapter from start
    // If audio is playing, restart it.
    // If chapter is animated, reset the timeline.
    this.actions.pipe(
      ofActionDispatched(PlayChapterFromStart),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.playChapterFromStart();
    });
    
    // display
    this.engineDisplay$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((display) => {
        this.display = display;
        this.changeDetecton.markForCheck();
      });

    // visibility
    this.engineVisible$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((visible) => {
        this.engineVisible = visible;
        this.changeDetecton.markForCheck();
      });

    this.visibilityLayersService.initObservables();

    // Contextmenu
    this.contextmenu$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((contextmenu) => {
        this.contextmenuNode = contextmenu.node;
        this.contextmenuHit = contextmenu.hit;
        this.changeDetecton.markForCheck();
      });
  }

  bindChapterChangeEvents() {
    // New chapter
    this.selectedChapter$
      .pipe(
        filter((chapter) => !isNil(chapter)),
        pairwise(),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([lastChapter, currentChapter]) => {
        const engineChapterId = this.chapterService.getCurrentChapterId();
        const lastChapterId = lastChapter.id;
        const currentChapterId = currentChapter.id;

        // ignore if current chapter activated in engine
        if (lastChapterId === currentChapterId && engineChapterId === currentChapterId) {
          return;
        }

        this.store.dispatch(new NodeActions.SetActiveChapter(currentChapterId));
        disableXrayMode(this.store, this.engineService);

        Human.timeline.scrub({ chapterId: currentChapterId });

        // after Undo, prevent the camera from flying to a previous chapter's camera.
        this.store.dispatch(new SetCameraFlyTo(undefined));
      });


    this.clippingPlanes$
      .pipe(
        filter(clippingPlanes => clippingPlanes !== undefined),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(clips => {
        const clipsParams = Object.values(clips.clips);
        const clipsEnabled = clipsParams.filter(clip => clip.enabled);
        Human.renderer.clip.reset();

        clipsEnabled.forEach(clip =>
          Human.renderer.clip.setClip({
            boundary: clips.boundary,
            ...clip
          })
        );
      });

    // Init chapter animation once it's loaded
    // ---
    // Should only fire when
    // - The readyState of a chapter changes
    // - Autoplay is set to true
    combineLatest(this.selectedChapterId$, this.readyState$, this.autoplay$)
      .pipe(
        filter(([chapterId, readyState, autoplay]) => 
          !isNil(chapterId) && 
          !isNil(readyState) && 
          !isNil(autoplay)
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([, readyState, engineAutoplay]) => {
        const animationReady = get(readyState, 'animation', false);
        
        // When no autoplay is defined one way or another, assume the intent was for the chapter to animate
        const chapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
        const chapterAutoplay = get(chapter, 'ui.play.autoplay', true);

        // Consolidate all play triggers
        const shouldPlay = animationReady && engineAutoplay && chapterAutoplay;
        this.store.dispatch(new PlayChapterAnimation(chapter, !shouldPlay));
        this.store.dispatch(new ToggleAnimation(shouldPlay));
      });
  }

  bindCameraEvents() {
    this.actions.pipe(
      ofActionDispatched(SetCamera),
      filter((action: SetCamera) => !isNil(action.camera)),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(
      (action: SetCamera) => {
        action.animate
        ? this.flyToPoint(action.camera, action.animationDuration)
        : this.jumpToPoint(action.camera);

      }
    );

    this.actions.pipe(
      ofActionDispatched(PanCameraStatic),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action: PanCameraStatic) => {
      this.cameraService.panCameraStatic(action.x, action.y, action.z);
    });

    this.actions.pipe(
      ofActionDispatched(RotateCameraStatic),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action: RotateCameraStatic) => {
      this.cameraService.rotateCameraStatic(action.x, action.y);
    });

    this.actions.pipe(
      ofActionDispatched(ZoomCameraStatic),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action: ZoomCameraStatic) => {
      this.cameraService.setHumanZoomFactor(action.value);
    });

    this.actions.pipe(
      ofActionDispatched(FlyToObject),
      filter((action: FlyToObject) => !!action.objectId),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action: FlyToObject) => {
      action.animate
      ? this.flyToObject(action.objectId, action.animationDuration)
      : this.jumpToObject(action.objectId);
    });
  }
  
  bindToolEvents() {
    // Selected tool changed
    this.activeTool$
    .pipe(
      takeUntil(this.ngUnsubscribe)
    )
    .subscribe((tool) => {
      // Reset hover class
      Object.values(CursorClasses).forEach(cursorClass => {
        if (cursorClass) {
          this.renderer.removeClass(this.engineElement.nativeElement, cursorClass);
        }
      });

      switch (tool) {
        case ToolEnum.Hide:
          this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Hide);
          break;
        case ToolEnum.Label:
          this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Label);
          break;
        case ToolEnum.Paint:
          this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Paint);
          break;
        case ToolEnum.Quiz:
          this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Crosshair);
          break;
        default:
          // Do nothing for now
          break;
      }
    });
  }

  bindMouseEvents() {
    // User moves mouse, debounce to small delay so lookup only occurs on pause.
    // Done for performance, doing a node lookup in 3D is expensive.
    this.mouseMove$ = fromEvent<MouseEvent>(this.engineElement.nativeElement, 'mousemove');

    // Handle all mouse move events, primarily for panning the camera
    this.mouseMove$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((event) => {
      this.handleMousePan(event);
    });
      
      
    // User moves mouse, debounce to small delay so  hover lookup only occurs on pause.
    // Done for performance, doing a node lookup in 3D is expensive.
    this.mouseMove$.pipe(
      debounceTime(10),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((event) => { 
      this.handleMouseHover(event);
    });

    // User leaves scene, clear hovered node
    this.mouseLeave$ = fromEvent<MouseEvent>(this.engineElement.nativeElement, 'mouseleave');
    this.mouseLeave$.pipe(
      debounceTime(10),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(event => {
      this.handleMouseLeave();
    });

  }

  bindKeyEvents() {
    const downKeys = ['w', 'a', 's', 'd', 'e', 'c', '=', '-', '+', '_', 'arrowleft', 'arrowright', 'arrowup', 'arrowdown', 'shiftKey']
      .reduce((result, value) => {
        result[value] = false;
        return result;
      }, {});

    const downKeys$ = new BehaviorSubject<any>(downKeys);

    let lastvalue: any;

    this.renderer.listen(window, 'keydown', (event) => {
      if (event.repeat || event.metaKey || event.ctrlKey) {
        return;
      }
      
      // Prevent keyboard events while inputs are focused
      const eventNode = event.target.nodeName.toLowerCase();
      if (['input', 'textarea', 'select', 'option'].indexOf(eventNode) >= 0) {
        return;
      }

      downKeys$.next({
        ...downKeys$.value,
        [event.key.toLowerCase()]: true,
        shiftKey: event.shiftKey,
        hasMomentum: false,
      });
    });

    // only process the keyup event if there was only one key pressed previously
    this.renderer.listen(window, 'keyup', (event: KeyboardEvent) => {
      if (event.key) {
        const numPressed = Object.values(lastvalue).filter((a) => a === true)
          .length;
        if (numPressed === 1) {
          this.applyMotion(lastvalue, true);
          downKeys$.next({
            ...downKeys$.value,
            [event.key.toLowerCase()]: false,
            shiftKey: event.shiftKey,
            hasMomentum: lastvalue.shiftKey === false,
          });
        } else {
          downKeys$.next({
            ...downKeys$.value,
            [event.key.toLowerCase()]: false,
            shiftKey: event.shiftKey,
            hasMomentum: false,
          });
        }
      }
    });

    downKeys$
      .pipe(
        // validate input enabled state
        filter(() => this.inputEnabled),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((value) => {
        lastvalue = value;
        if (value.hasMomentum === false) {
          this.applyMotion(value, false);
        }
      });

      this.renderer.listen(window, 'keydown', (e) => {
      if (e.key === 'Control') {
        this.ctrlPressed = true;
      }
    });

    this.renderer.listen(window, 'keyup', (e) => {
      if (e.key === 'Control') {
        this.ctrlPressed = false;
      }
    });
    
  }

  // apply keyboard input to the camera motion model
  // momentum will be true on a keyup event 
  applyMotion(value, momentum) {
    const motion = new CameraMotionModel();
    const zoom = value.shiftKey ? KeyboardCamera.ZoomSlow : KeyboardCamera.Zoom;
    const pan = value.shiftKey ? KeyboardCamera.PanSlow : KeyboardCamera.Pan;
    const rotate = value.shiftKey
      ? KeyboardCamera.RotateSlow
      : KeyboardCamera.Rotate;

    motion.zoom += value.c || value['-'] || value['_'] ? zoom : 0;
    motion.zoom -= value.e || value['='] || value['+'] ? zoom : 0;

    motion.panX -= value.d ? pan : 0;
    motion.panX += value.a ? pan : 0;

    motion.panY += value.w ? pan : 0;
    motion.panY -= value.s ? pan : 0;

    motion.rotateX -= value.arrowright ? rotate : 0;
    motion.rotateX += value.arrowleft ? rotate : 0;

    motion.rotateY += value.arrowup ? rotate : 0;
    motion.rotateY -= value.arrowdown ? rotate : 0;

    motion.applyFriction = momentum;

    this.store.dispatch(new MoveCamera(motion));
  }

  initChangeListener() {
    // listen for state changes
    // listen for state changes
    this.store
    .select(EngineNodesState.getPatches)
    .pipe(
      filter((patches) => patches.length > 0),
      takeUntil(this.ngUnsubscribe)
    )
    .subscribe((patches) => {
      const patchesForProp = patches.reduce(
        (acc, patch) => {
          const patchProp = patch.path[patch.path.length - 1];
          if (acc[patchProp] !== undefined) {
            acc[patchProp].push(patch);
          }
          return acc;
        },
        {
          shown: [],
          checked: [],
          paint: [],
          pickable: [],
        }
      );

      const getDiff = (prop: 'shown' | 'checked' | 'paint' | 'pickable') =>
        patchesForProp[prop].reduce((acc, patch) => {
          const objectId = patch.path[2];
          acc[objectId] = patch.value;
          return acc;
        }, {});

      const diffChecked: Record<ObjectId, boolean> = getDiff('checked');
      const diffShown: Record<ObjectId, boolean> = getDiff('shown');
      const diffPaint: Record<ObjectId, ColorRecipeModel> = getDiff('paint');
      const diffPickable: Record<ObjectId, boolean> = getDiff('pickable');

      // Set object visibility, and update the view graph
      if (!isEmpty(diffShown)) {
        const currentNodes = this.store.selectSnapshot(EngineNodesState.getNodes);
        const shownState = Object.values(currentNodes).reduce(
          (acc, nodeState) => {
            acc[nodeState.objectId] = nodeState.shown;
            return acc;
          },
          {}
        );
        this.engineService.setNodesVisible(shownState);
        const stateMap = this.convertToLeafStateMap('shown', shownState);
        this.store.dispatch(new SynchronizeVisibleNodes(stateMap, currentNodes));
      }

      // Set object highlight
      if (!isEmpty(diffChecked)) {
        this.engineService.fireEvent(EventsEnum.SceneObjectsSelected, {
          selectedObjectsUpdate: diffChecked,
        });

        const highlightMethod = this.store.selectSnapshot(
          EngineState.getXrayModeEnabled
        )
          ? (objectId, value) =>
              this.paintService.paint(
                objectId,
                value === true
                  ? PaintPresetOriginal.recipe
                  : PaintPresetFade.recipe
              )
          : (objectId, value) => this.paintService.highlight(objectId, value);

        Object.entries(diffChecked).forEach(([objectId, value]) => {
          highlightMethod(objectId, value);
        });
      }

      // Set object color recipes
      if (!isEmpty(diffPaint)) {
        for (const [objectId, recipe] of Object.entries(diffPaint)) {
          this.paintService.colorObject(objectId, ColorRecipeModel.toEngine(recipe));
        }
      }

      // Set object pickability, and update the view graph.
      if (!isEmpty(diffPickable)) {
        const currentNodes = this.store.selectSnapshot(EngineNodesState.getNodes);
        const pickableState = Object.values(currentNodes).reduce(
          (acc, nodeState) => {
            acc[nodeState.objectId] = nodeState.pickable;
            return acc;
          },
          {}
        );
        this.engineService.setNodesPickable(pickableState);
        this.store.dispatch(
          new SynchronizePickableNodes(
            this.convertToLeafStateMap('pickable', pickableState),
            currentNodes
          )
        );
      }
    });
  }

  initChapterAudio() {
    combineLatest(
      this.selectedChapterId$,
      this.playAudio$
    )
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([chapterId, playAudio]) => {
        this.playAudio(playAudio);
      });
  }

  initBackgroundListener() {
    combineLatest([this.background$, this.engineDisplay$, this.engineVisible$])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([background, display, engineVisible]) => {
        this.renderer.setStyle(this.engineElement.nativeElement, 'background-image', BackgroundModel.toCSS(background));

        if (this.isBackgroundCheckered(background)) {
          this.renderer.addClass(this.engineElement.nativeElement, 'engine--checkered');
        } else {
          this.renderer.removeClass(this.engineElement.nativeElement, 'engine--checkered');
        }
        
        this.renderer.setStyle(this.engineElement.nativeElement, 'display', display ? 'block' : 'none');
        this.renderer.setStyle(this.engineElement.nativeElement, 'opacity', engineVisible ? 1 : 0);
      });
  }

  initPlaybackSpeedListener() {
    this.store.select(
      EngineState.getPlaybackSpeed
    ).pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(speed => {
      Human.timeline.scale = speed;
    });
  }

  repositionEngine(position: EnginePositionModel) {
    this.renderer.setStyle(this.engineElement.nativeElement, 'left', `${position.left}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'top', `${position.top}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'width', `${position.width}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'height', `${position.height}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'zIndex', `${position.visibility}`);
    this.renderer.setStyle(
      this.engineElement.nativeElement, 'visibility', (position.visibility === EngineVisibilityEnum.Shown) ? 'visible' : 'hidden'
    );

    // Engine user interaction will be disabled when the engine isn't visible
    this.visible = position.visibility === EngineVisibilityEnum.Shown;
    Human.renderer.resize();
  }

  /**
   * User Interactions
   * Depends on the HammerJS library to ensure touch compatibility, and manage pan handlers (heh...)
   */
  bindHumanInteractions() {
    // Create a HammerJS manager so we can custom define single and double click
    const mc = new Hammer.Manager(this.engineCanvasElement.nativeElement);

    // Tap recognizer with minimal 2 taps
    mc.add(new Hammer.Tap({ event: 'doubletap', taps: 2, posThreshold: 50 }));

    // Single tap recognizer
    mc.add(new Hammer.Tap({ event: 'singletap' }));

    // Pan recognizer
    mc.add(new Hammer.Pan({ event: 'pan', pointers: 0 }));

    // Pinch
    mc.add(new Hammer.Pinch({ event: 'pinch' }));

    // Press
    mc.add(new Hammer.Press({ time: 500 }));

    
    mc.on('singletap', (event) => {
      this.handleTap(event);
    });

    mc.on('doubletap', (event)  => {
      this.handleDoubleTap(event);
    });

    mc.on('press', (event) => {
      event.preventDefault();
      event.srcEvent.stopImmediatePropagation();
      this.handlePress(event);
    });

    mc.on('pan', (event) => {
      event.preventDefault();
      this.handlePan(event);
    });
    
    mc.on('panstart', (event) => {
      this.handlePanStart(event);
    });

    mc.on('panend', (event) => {
      this.handlePanEnd(event);
    });

    mc.on('pinchend', (event) => {
      this.handlePinchEnd(event);
    });

    let cacheScale = 0;
    let scaleDir = 0;
    let cacheX = 0;
    let cacheY = 0;

    mc.on('pinchstart', (event) => {
      cacheScale = event.scale;
      cacheX = event.deltaX;
      cacheY = event.deltaY;
      this.pinchMode = '';
    });

    mc.on('pinchmove', (event) => {
      if (this.pinchTicks > 0) {
        return;
      }
      const panDistanceMet =
        Math.abs(event.deltaX) > 60 || Math.abs(event.deltaY) > 60;
      const pinchScaleMet = event.scale > 1.25 || event.scale < 0.75;

      // Determine is user is panning or pinching, used for momentum after the gesture
      if (!this.pinchMode) {
        this.pinchMode = pinchScaleMet ? 'pinch' : 'pan';
      } else {
        if (this.pinchMode === 'pan' && pinchScaleMet && !panDistanceMet) {
          this.pinchMode = 'pinch';
        }

        if (this.pinchMode === 'pinch' && panDistanceMet && !pinchScaleMet) {
          this.pinchMode = 'pan';
        }
      }

      const scale = event.scale - cacheScale;
      cacheScale = event.scale;

      // ignore scale if it is bouncing back and forth between positive and negative
      const lastDir = scaleDir;
      if (scale === 0) {
        scaleDir = 0;
      } else {
        scaleDir = scale > 0 ? 1 : -1;
      }
      this.cameraService.setCameraPan(
        event.velocityX * TouchCamera.Pan,
        event.velocityY * TouchCamera.Pan
      );
      if (scaleDir + lastDir !== 0) {
        this.cameraService.setHumanZoom(scale * TouchCamera.Zoom);
      }
    });
  }

  handleMouseHover(event: MouseEvent) {
    if (event.buttons !== MouseButtons.None) {
      return;
    }

    const { offsetX, offsetY} = event;
    const hit = Human.renderer.pick.pick(offsetX, offsetY);

    if (hit.objectId) {
      const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
      const hitNode = this.engineService.getNode(viewGraph, hit.objectId);
      const hoveredNode = this.store.selectSnapshot(EngineState.getHoveredNode);

      if (!hoveredNode || hoveredNode.id !== hitNode.id) {
        this.store.dispatch(new HoverNode(hitNode, hit));
      }
    } else {
      if (this.store.selectSnapshot(EngineState.getHoveredNode) != null) {
        this.store.dispatch(new HoverNode(null));
      }
    }
  }

  handleMouseLeave() {
    this.store.dispatch(new HoverNode(null));
  }

  handleMousePan(event: MouseEvent) {
    // Left mouse button is handled by HammerJS 'pan'
    if (event.buttons === 2 || event.buttons === 4) {
      let { movementX, movementY } = event;
      movementX /= 16;
      movementY /= 16;
      this.cameraService.setCameraPan(movementX, movementY);
    }
  }

  handleMouseOut() {
    this.store.dispatch(new HoverNode(null));
  }

  // When it comes to wheel events, we account for both trackpad and scroll wheel. The deltaY
  // from these events can vary wildly between trackpad, mouse type, and browser. Read the docs
  // below to learn more.
  // https://developer.mozilla.org/en-US/docs/Web/API/WheelEvent
  handleWheel(event: WheelEvent) {
    let deltaY = 0;

    // Check deltaMode, which determines if we're pixel / line / page scrolling
    switch (event.deltaMode) {
      case 0: // Pixel delta (most common, trackpad and mice scroll wheels in Chrome & Safari)
        
        // Best attempt at determining trackpad vs wheel. 
        // On initial scroll, a trackpad never starts off higher than 10 pixels movement.
        if (this.scrollType === null) {
          this.scrollType = (Math.abs(event.deltaY) < 10) ? 'trackpad' : 'wheel';
        }

        // If trackpad, use deltaY
        // Otherwise use fixed scroll speed
        deltaY = (this.scrollType === 'trackpad') ?
          event.deltaY * TouchCamera.TrackZoom :
          Math.sign(event.deltaY) * MouseCamera.Zoom;
        break;
      
      default: // Line delta (less common, mice scroll wheels in Firefox)
        this.scrollType = 'wheel';
        deltaY = event.deltaY * MouseCamera.Zoom;
        break;
    }

    
    // Reset checking tracker. After 500ms of inactivity, reset scroll tracker.
    // Do this so that users transitioning between mouse and trackpad don't have issues.
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.scrollType = null;
    }, 500);

    // Don't allow zoom to exceed max
    if (Math.abs(deltaY) > 40) {
      deltaY = Math.sign(deltaY) * 40;
    }
    

    // For trackpad, use pixel precision.
    if (this.scrollType === 'trackpad') {
      this.cameraService.setCameraZoom(deltaY);
    
    // For wheel, use fixed velocity
    } else {
      this.zoomWithVelocity(deltaY);
    }
  }

  zoomWithVelocity(deltaY) {
    this.cameraService.setCameraZoom(deltaY);

    requestAnimationFrame(() => {
      deltaY *= 0.85;
      if (Math.abs(deltaY) > 1) {
        this.zoomWithVelocity(deltaY);
      }
    });
  }

  handleContextMenu(event: MouseEvent) {
    event.preventDefault();
    const { offsetX, offsetY} = event;
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);

    this.store.dispatch(new SetContextMenu(node, hit));
  }

  handlePanStart(event: HammerInput) {}

  handlePanEnd(event: HammerInput) {
    const motion = this.store.selectSnapshot(EngineCameraState.getCameraMotion);
    if (
      this.pinchTicks > 0 ||
      (motion.panX === event.velocityX && motion.panY === event.velocityY)
    ) {
      // this was a pinch, however the panend event also fires
      return;
    }
    const panMode = this.store.selectSnapshot(EngineCameraState.getPanMode);
    if (event.srcEvent.shiftKey || panMode === 'pan') {
      motion.panX = event.velocityX * TouchCamera.Pan;
      motion.panY = event.velocityY * TouchCamera.Pan;
    } else {
      motion.rotateX = event.velocityX * TouchCamera.Rotate;
      motion.rotateY = event.velocityY * TouchCamera.Rotate;
    }
    motion.applyFriction = true;
    this.store.dispatch(new MoveCamera(motion));
  }

  handlePinchEnd(event: HammerInput) {
    const motion = this.store.selectSnapshot(EngineCameraState.getCameraMotion);
    if (this.pinchMode === 'pan') {
      motion.panX = event.velocityX * TouchCamera.Pan;
      motion.panY = event.velocityY * TouchCamera.Pan;
    } else {
      const sign = event.velocity > 0 ? 1 : -1;
      motion.zoom =
        Math.sqrt(
          event.velocityX * event.velocityX + event.velocityY * event.velocityY
        ) *
        -sign *
        TouchCamera.Zoom;
    }
    motion.applyFriction = true;
    this.store.dispatch(new MoveCamera(motion));
    this.pinchTicks = 10;
  }

  handleTap(event: HammerInput) {
    const { offsetX, offsetY} = this.getOffset(event);
    
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);

    this.store.dispatch(new ClickNode(node, event, hit));
  }

  handleDoubleTap(event: HammerInput) { // John Wick approved!
    const { offsetX, offsetY} = this.getOffset(event);
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);

    if (node) {
      this.store.dispatch(new FlyToObject(node.id));
    }
  }

  handlePress(event: HammerInput) {
    const { offsetX, offsetY} = this.getOffset(event);
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);

    if (node) {
      this.store.dispatch(new FlyToObject(node.id));
    }
  }
  
  handlePan(event: HammerInput) {
    if (this.pinchTicks > 0) {
      // this was a pinch, this event seems to fire if the two fingers aren't removed at exactly the same time
      return;
    }

    const panMode = this.store.selectSnapshot(EngineCameraState.getPanMode);
    const { velocityX, velocityY } = event;

    if (event.srcEvent.shiftKey || panMode === 'pan') {
      this.cameraService.setCameraPan(
        velocityX * TouchCamera.Pan,
        velocityY * TouchCamera.Pan
      );
    } else {
      this.cameraService.setCameraRotation(
        velocityX * TouchCamera.Rotate,
        velocityY * TouchCamera.Rotate
      );
    }
  }

  // forces are applied based on actual frame rate as compared to 60FPS so all models behave the same
  handleMouseInputEngineTick() {
    const rotation = this.cameraService.tickCameraRotation();
    const pan = this.cameraService.tickCameraPan();
    const zoom = this.cameraService.tickCameraZoom();

    // during load, this code is running, and can produce very large values of dt
    // this was causing the models to spin out of control
    const dt = Math.min(maxForceMultiplier, (window.performance.now() - this.deltaTime) / timeElapsedAtSixtyFPS);

    // Rotation
    if (rotation.vx || rotation.vy) {
      this.cameraService.rotateCameraStatic(
        rotation.vx !== 0 ? dt * rotation.vx * MouseCamera.Rotate : 0,
        rotation.vy !== 0 ? dt * rotation.vy * MouseCamera.Rotate : 0
      );
    }

    // Pan
    if (pan.x || pan.y) {
      const f = Math.max(Human.renderer.camera.lookDistance / 80, 0.01);
      this.cameraService.panCameraStatic(
        dt * pan.x * f * MouseCamera.Pan,
        dt * pan.y * f * MouseCamera.Pan,
        dt * pan.z * f * MouseCamera.Pan
      );
    }

    // Zoom
    if (zoom !== 0) {
      this.cameraService.setHumanZoom(dt * zoom * MouseCamera.Zoom);
    }
  }

  handleCameraTick() {
    if (this.pinchTicks > 0) {
      this.pinchTicks--;
    }
    this.applyFriction();
    const motion = this.store.selectSnapshot(EngineCameraState.getCameraMotion);
    this.cameraService.setCameraPan(motion.panX, motion.panY);
    this.cameraService.setCameraRotation(motion.rotateX, motion.rotateY);
    this.cameraService.setCameraZoom(motion.zoom);
  }

  // apply friction to all movement values, using frame rate
  //
  // we use dt as a multiplier on forces, 1.0 means we are running at 60fps
  //    larger values mean we are running slower
  //
  // usually this is expressed as:
  //    motion = motion * friction * dt
  //
  // here, for values of dt larger than 1.1765 this results in increasing rather than decresing the movement
  //
  // so instead the friction value is modified by a small amount based on dt
  //
  // for values over 1, we are adding more friction by adding a negative value to the friction multiplier
  //    and for values under 1 we are adding less than 0.1 to the multiplier, so it will always
  //    decrease the initial friction force which is 0.85
  private getFriction(value: number) {
    if (value === 0) {
      return 0;
    }

    const dt = (window.performance.now() - this.deltaTime) / timeElapsedAtSixtyFPS;
    // don't allow the friction to go below 0
    const friction = Math.max(0, (TouchCamera.Friction + 0.1 * (1 - dt)));
    let newValue = value * friction;

    if (Math.abs(newValue) < TouchCamera.FrictionThreshold) {
      newValue = 0;
    }

    return newValue;
  }

  applyFriction() {
    const motion = this.store.selectSnapshot(EngineCameraState.getCameraMotion);
    if (motion.applyFriction !== true) {
      return;
    }

    // Rotate
    motion.rotateX = this.getFriction(motion.rotateX);
    motion.rotateY = this.getFriction(motion.rotateY);

    // Pan
    motion.panX = this.getFriction(motion.panX);
    motion.panY = this.getFriction(motion.panY);

    // Zoom
    motion.zoom = this.getFriction(motion.zoom);

    if (
      motion.rotateX === 0 &&
      motion.rotateY === 0 &&
      motion.panX === 0 &&
      motion.panY === 0 &&
      motion.zoom === 0
    ) {
      motion.applyFriction = false;
    }

    this.store.dispatch(new MoveCamera(motion));
  }

  // Apply friction to the momentum and eventually come to a stop
  // Forces are applied based on actual frame rate so all models behave the same
  // applyFriction() {
  //   const motion = this.store.selectSnapshot(EngineCameraState.getCameraMotion);
  //   if (motion.applyFriction !== true) {
  //     return;
  //   }
    
  //   const dt =
  //     (window.performance.now() - this.deltaTime) / timeElapsedAtSixtyFPS;

  //   // Rotate
  //   if (motion.rotateX !== 0 || motion.rotateY !== 0) {
  //     motion.rotateX = motion.rotateX * (TouchCamera.Friction + 0.1 * (1 - dt));
  //     motion.rotateY = motion.rotateY * (TouchCamera.Friction + 0.1 * (1 - dt));
  //     if (Math.abs(motion.rotateX) < TouchCamera.FrictionThreshold) {
  //       motion.rotateX = 0;
  //     }
  //     if (Math.abs(motion.rotateY) < TouchCamera.FrictionThreshold) {
  //       motion.rotateY = 0;
  //     }
  //   }

  //   // Pan
  //   if (motion.panX !== 0 || motion.panY !== 0) {
  //     motion.panX = motion.panX * (TouchCamera.Friction + 0.1 * (1 - dt));
  //     motion.panY = motion.panY * (TouchCamera.Friction + 0.1 * (1 - dt));
  //     if (Math.abs(motion.panX) < TouchCamera.FrictionThreshold) {
  //       motion.panX = 0;
  //     }
  //     if (Math.abs(motion.panY) < TouchCamera.FrictionThreshold) {
  //       motion.panY = 0;
  //     }
  //   }

  //   // Zoom
  //   if (motion.zoom !== 0) {
  //     motion.zoom = motion.zoom * (TouchCamera.ZoomFriction + 0.1 * (1 - dt));
  //     if (Math.abs(motion.zoom) < TouchCamera.FrictionThreshold) {
  //       motion.zoom = 0;
  //     }
  //   }

  //   if (
  //     motion.rotateX === 0 &&
  //     motion.rotateY === 0 &&
  //     motion.panX === 0 &&
  //     motion.panY === 0 &&
  //     motion.zoom === 0
  //   ) {
  //     motion.applyFriction = false;
  //   }

  //   this.store.dispatch(new MoveCamera(motion));
  // }

  flyToObject(objectId: string, animationDuration?: number) {
    Human.renderer.camera.fly.flyTo({
      objectId,
      animationDuration
    });
  }
  
  jumpToObject(objectId: string) {
    Human.renderer.camera.fly.jumpTo({ objectId });
  }

  flyToPoint(destination: CameraModel, animationDuration?: number) {
    Human.renderer.camera.fly.flyTo({
      ...destination,
      animationDuration
    }, () => {
      this.store.dispatch(new SetCameraSuccess());
    });
  }

  jumpToPoint(destination: CameraModel) {
    Human.renderer.camera.fly.jumpTo(destination);
  }

  getNodeFromHit(hit: HitModel) {
    let node: ViewTreeNode;

    if (hit.objectId) {
      const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
      node = this.engineService.getNode(viewGraph, hit.objectId);
    }

    return node;
  }

  getHit(x, y) {
    // For clicks, set rayPick to true so we get a completely detailed hit object
    // that includes the exact triangle the user clicked on. This is different 
    // from hover, that only target the general node. 
    const hit = Human.renderer.pick.pick(x, y, {rayPick: true }) as HitModel;
    return hit;
  }

  // We calculate offsetX and offsetY using rect and center because iOS safari
  // does not natively support offsetX and offsetY. Those properties return `undefined`
  // when requested. 
  // More: http://www.jacklmoore.com/notes/mouse-position/
  getOffset(event: any): {offsetX: number, offsetY: number} {
    let x = 0;
    let y = 0;

    if (event.center) {
      x = event.center.x;
      y = event.center.y;
    } else {
      x = event.clientX;
      y = event.clientY;
    }

    const target = <HTMLDivElement>this.engineElement.nativeElement;
    const rect = target.getBoundingClientRect();
    
    return {
      offsetX: x - rect.left,
      offsetY: y - rect.top
    };
  }

  playAudio(play?: boolean) {
    play = !isNil(play) ? play : this.store.selectSnapshot(EngineState.getPlayAudio);

    // Stop all players to ensure we don't have multiple playing at once
    this.mediaService.stopAudio();

    // Instantiate audio player first time chapter is viewed, then cache.
    const audio = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterAudio);

    // Ensure audio has a full url
    const src: string = get(audio, 'data.fullUrl');

    if (src) {
      // check for existing audio player
      let audioPlayer: Howl = this.mediaService.getAudioPlayer(src);

      // create audio player if required
      if (!audioPlayer) {
        audioPlayer = this.mediaService.addAudioPlayer(src);
      }

      // Play or pause audio
      (play) ? audioPlayer.play() : audioPlayer.stop();
    }
  }

  playChapterFromStart() {
    const chapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);

    this.playAudio();
    this.store.dispatch(new PlayChapterAnimation(chapter));
    this.store.dispatch(new ToggleAnimation(true));
    
  }

  isTransparentBackground(background: BackgroundModel): boolean {
    return background ? BackgroundModel.isTransparent(this.background.getValue()) : false;
  }
  
  isBackgroundCheckered(background: BackgroundModel): boolean {
    return environment.application !== EnvironmentApplication.Viewer && this.isTransparentBackground(background);
  }
}
